import { ApiClient } from '@momonos/api-client'
import {
  createTranslator,
  DEFAULTS,
  formatBdt,
  type Locale,
  type MenuResponse,
  type Product,
} from '@momonos/shared'
import { Badge, Button, cn, FullscreenLoader, Modal, SpiceLevel } from '@momonos/ui'
import { ChevronLeft, Flame, Leaf, Minus, Plus, ShoppingCart, Star } from 'lucide-react'
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { API_URL, BRANCH_ID } from './lib/config'
import {
  customizeUnitPrice,
  idleStateFor,
  type KioskAction,
  type KioskSession,
  kioskReducer,
  newSession,
  secondsUntilReset,
  sessionTotals,
} from './lib/session'
import './styles.css'

const IDLE_TIMEOUT_SEC = DEFAULTS.kioskIdleTimeoutSec

export function App() {
  const [session, dispatch] = useReducer(kioskReducer, undefined, () => newSession())
  const [menu, setMenu] = useState<MenuResponse | null>(null)
  const [categoryId, setCategoryId] = useState<string | null>(null)
  const [now, setNow] = useState(() => Date.now())
  const [isSubmitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const idempotencyKey = useRef(crypto.randomUUID())

  const t = useMemo(() => createTranslator(session.locale), [session.locale])
  const api = useMemo(() => new ApiClient({ baseUrl: API_URL }), [])

  useEffect(() => {
    api
      .get<MenuResponse>('/v1/menu', { query: { branch: BRANCH_ID } })
      .then((data) => {
        setMenu(data)
        setCategoryId(data.categories[0]?.id ?? null)
      })
      .catch(() => setError('NETWORK'))
  }, [api])

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [])

  // FR-KIO-1 — clear an abandoned order so the next customer starts fresh.
  const idle = idleStateFor(session, now, IDLE_TIMEOUT_SEC)
  useEffect(() => {
    if (idle === 'expired') {
      idempotencyKey.current = crypto.randomUUID()
      dispatch({ type: 'reset' })
    }
  }, [idle])

  const totals = sessionTotals(session, menu)

  const placeOrder = useCallback(async () => {
    if (!menu || session.lines.length === 0) return

    setSubmitting(true)
    setError(null)

    try {
      const result = await api.post<{
        order: { orderNumber: string }
        statusToken: string
        estimatedWaitSeconds: number
      }>(
        '/v1/orders',
        {
          branchId: menu.branch.id,
          channel: 'kiosk',
          type: session.orderType,
          couponCode: session.couponCode,
          lines: session.lines.map((line) => ({
            productId: line.product.id,
            variantId: line.variantId,
            quantity: line.quantity,
            modifierIds: line.modifierIds,
            note: line.note,
          })),
        },
        // FR-ORD-6 — a double tap on a touchscreen must not order twice.
        { idempotencyKey: idempotencyKey.current },
      )

      dispatch({
        type: 'order-placed',
        orderNumber: result.order.orderNumber,
        statusToken: result.statusToken,
        estimatedWaitSeconds: result.estimatedWaitSeconds,
      })
      idempotencyKey.current = crypto.randomUUID()
    } catch (caught) {
      const code = (caught as { code?: string }).code ?? 'INTERNAL'
      setError(code)
    } finally {
      setSubmitting(false)
    }
  }, [api, menu, session])

  if (!menu) return <FullscreenLoader label="…" />

  const locale = session.locale
  const products = menu.products.filter((product) => product.categoryId === categoryId)

  return (
    <div lang={locale} className="min-h-dvh bg-surface touch-surface">
      {session.screen === 'attract' && (
        <Attract
          onStart={(chosen) => dispatch({ type: 'start', locale: chosen })}
          locale={locale}
        />
      )}

      {session.screen === 'order-type' && (
        <OrderTypeScreen
          t={t}
          onChoose={(orderType) => dispatch({ type: 'set-order-type', orderType })}
        />
      )}

      {session.screen === 'menu' && (
        <MenuScreen
          menu={menu}
          locale={locale}
          t={t}
          categoryId={categoryId}
          products={products}
          itemCount={totals.itemCount}
          total={totals.total}
          onCategory={setCategoryId}
          onSelect={(product) => dispatch({ type: 'begin-customize', product })}
          onCart={() => dispatch({ type: 'go', screen: 'cart' })}
        />
      )}

      {session.screen === 'customize' && session.customizing && (
        <CustomizeScreen session={session} locale={locale} t={t} dispatch={dispatch} />
      )}

      {session.screen === 'cart' && (
        <CartScreen
          session={session}
          locale={locale}
          t={t}
          totals={totals}
          dispatch={dispatch}
          onPay={() => dispatch({ type: 'go', screen: 'payment' })}
        />
      )}

      {session.screen === 'payment' && (
        <PaymentScreen
          t={t}
          locale={locale}
          total={totals.total}
          isSubmitting={isSubmitting}
          error={error}
          onBack={() => dispatch({ type: 'go', screen: 'cart' })}
          onConfirm={placeOrder}
        />
      )}

      {session.screen === 'success' && session.placedOrder && (
        <SuccessScreen
          t={t}
          locale={locale}
          orderNumber={session.placedOrder.orderNumber}
          waitSeconds={session.placedOrder.estimatedWaitSeconds}
          onDone={() => dispatch({ type: 'reset' })}
        />
      )}

      {/* FR-KIO-1 — warn before wiping an order the customer is still choosing. */}
      <Modal
        isOpen={idle === 'warning'}
        onClose={() => dispatch({ type: 'go', screen: session.screen })}
        title={t('kiosk.stillThere')}
        dismissOnBackdrop={false}
        footer={
          <Button size="lg" onClick={() => dispatch({ type: 'go', screen: session.screen })}>
            {t('kiosk.continueOrdering')}
          </Button>
        }
      >
        <p className="text-content-secondary text-lg">
          {t('kiosk.idleWarning', { seconds: secondsUntilReset(session, now, IDLE_TIMEOUT_SEC) })}
        </p>
      </Modal>
    </div>
  )
}

type Translator = ReturnType<typeof createTranslator>

/** FR-KIO-1 — the attract screen, with the language choice up front. */
function Attract({ onStart, locale }: { onStart: (locale: Locale) => void; locale: Locale }) {
  void locale
  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-10 p-12 text-center">
      <div className="animate-fade-in">
        <h1 className="font-bold text-8xl text-momo-white tracking-tight">MOMONOS</h1>
        <p className="mt-3 text-momo-yellow text-2xl">Made fresh, every order</p>
      </div>

      {/* FR-SPN-1 — the acquisition loop starts here: phone out, spin, coupon. */}
      <p className="text-content-muted text-lg">
        🎡 Spin &amp; Win a free momo —{' '}
        <span className="text-momo-yellow">order.momonos.com/spin</span>
      </p>

      <p className="text-content-muted text-xl">Choose your language · ভাষা বেছে নিন</p>

      <div className="flex gap-4">
        <Button size="kiosk" variant="primary" onClick={() => onStart('en')}>
          English
        </Button>
        <Button size="kiosk" variant="yellow" onClick={() => onStart('bn')} lang="bn">
          বাংলা
        </Button>
      </div>
    </main>
  )
}

function OrderTypeScreen({
  t,
  onChoose,
}: {
  t: Translator
  onChoose: (type: 'dine_in' | 'takeaway') => void
}) {
  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-10 p-12">
      <h2 className="font-bold text-5xl text-content">{t('kiosk.howToEat')}</h2>

      <div className="grid grid-cols-2 gap-6">
        {(['dine_in', 'takeaway'] as const).map((type) => (
          <button
            key={type}
            type="button"
            onClick={() => onChoose(type)}
            className="h-64 w-64 rounded-2xl border-2 border-surface-border bg-surface-raised font-semibold text-2xl text-content transition-colors hover:border-momo-red active:scale-[0.98]"
          >
            {t(type === 'dine_in' ? 'kiosk.dineIn' : 'kiosk.takeaway')}
          </button>
        ))}
      </div>
    </main>
  )
}

function MenuScreen({
  menu,
  locale,
  t,
  categoryId,
  products,
  itemCount,
  total,
  onCategory,
  onSelect,
  onCart,
}: {
  menu: MenuResponse
  locale: Locale
  t: Translator
  categoryId: string | null
  products: Product[]
  itemCount: number
  total: number
  onCategory: (id: string) => void
  onSelect: (product: Product) => void
  onCart: () => void
}) {
  return (
    <main className="flex min-h-dvh flex-col pb-28">
      <nav className="sticky top-0 z-10 flex gap-2 overflow-x-auto border-surface-border border-b bg-surface p-4">
        {menu.categories.map((category) => (
          <button
            key={category.id}
            type="button"
            onClick={() => onCategory(category.id)}
            className={cn(
              'h-touch-kiosk shrink-0 rounded-full px-6 font-semibold text-lg transition-colors',
              category.id === categoryId
                ? 'bg-momo-red text-momo-white'
                : 'bg-surface-raised text-content-secondary',
            )}
          >
            {category.name[locale]}
          </button>
        ))}
      </nav>

      <div className="grid grid-cols-3 gap-4 p-4">
        {products.map((product) => (
          <button
            key={product.id}
            type="button"
            disabled={!product.isAvailable}
            onClick={() => onSelect(product)}
            className={cn(
              'overflow-hidden rounded-2xl border border-surface-border bg-surface-raised text-left transition-transform active:scale-[0.98]',
              !product.isAvailable && 'opacity-40',
            )}
          >
            <div className="flex aspect-[4/3] items-center justify-center bg-surface-overlay">
              {product.imageUrl ? (
                <img
                  src={product.imageUrl}
                  alt=""
                  className="size-full object-cover"
                  loading="lazy"
                />
              ) : (
                <Flame aria-hidden="true" className="size-12 text-content-muted" />
              )}
            </div>

            <div className="p-4">
              <div className="mb-2 flex flex-wrap gap-1.5">
                {product.isBestseller && (
                  <Badge tone="yellow">
                    <Star aria-hidden="true" className="size-3 fill-current" />
                    {t('common.bestseller')}
                  </Badge>
                )}
                {product.isVeg && (
                  <Badge tone="green">
                    <Leaf aria-hidden="true" className="size-3" />
                    {t('common.veg')}
                  </Badge>
                )}
                {!product.isAvailable && <Badge tone="red">{t('common.soldOut')}</Badge>}
              </div>

              <h3 className="font-semibold text-content text-xl leading-tight">
                {product.name[locale]}
              </h3>

              <div className="mt-2 flex items-center justify-between">
                <span className="font-bold text-content text-xl">
                  {formatBdt(product.price, locale)}
                </span>
                {product.calories != null && (
                  <span className="text-content-muted text-sm">
                    {product.calories} {t('common.calories')}
                  </span>
                )}
              </div>

              <SpiceLevel
                level={product.spiceLevel}
                labels={[
                  t('customize.spice0'),
                  t('customize.spice1'),
                  t('customize.spice2'),
                  t('customize.spice3'),
                ]}
                className="mt-2"
              />
            </div>
          </button>
        ))}
      </div>

      {/* Floating cart — always reachable, never covering the item being read. */}
      {itemCount > 0 && (
        <button
          type="button"
          onClick={onCart}
          className="fixed inset-x-4 bottom-4 flex h-touch-kiosk items-center justify-between rounded-2xl bg-momo-red px-8 font-semibold text-momo-white text-xl shadow-glow-red"
        >
          <span className="flex items-center gap-3">
            <ShoppingCart aria-hidden="true" className="size-6" />
            {t('kiosk.yourOrder')} ({itemCount})
          </span>
          <span>{formatBdt(total, locale)}</span>
        </button>
      )}
    </main>
  )
}

function CustomizeScreen({
  session,
  locale,
  t,
  dispatch,
}: {
  session: KioskSession
  locale: Locale
  t: Translator
  dispatch: (action: KioskAction) => void
}) {
  const item = session.customizing
  if (!item) return null

  const unitPrice = customizeUnitPrice(session)

  return (
    <main className="flex min-h-dvh flex-col">
      <header className="flex items-center gap-3 border-surface-border border-b p-4">
        <Button variant="ghost" size="lg" onClick={() => dispatch({ type: 'cancel-customize' })}>
          <ChevronLeft aria-hidden="true" className="size-6" />
          {t('common.back')}
        </Button>
        <h2 className="font-bold text-2xl text-content">{item.product.name[locale]}</h2>
      </header>

      <div className="flex-1 space-y-8 overflow-y-auto p-6">
        {item.product.variants.length > 0 && (
          <section>
            <h3 className="mb-3 font-semibold text-content text-xl">{t('common.quantity')}</h3>
            <div className="flex flex-wrap gap-3">
              {item.product.variants.map((variant) => (
                <button
                  key={variant.id}
                  type="button"
                  disabled={!variant.isAvailable}
                  onClick={() => dispatch({ type: 'set-variant', variantId: variant.id })}
                  className={cn(
                    'h-touch-kiosk rounded-xl border-2 px-8 font-semibold text-lg',
                    variant.id === item.variantId
                      ? 'border-momo-red bg-momo-red/15 text-content'
                      : 'border-surface-border text-content-secondary',
                    !variant.isAvailable && 'opacity-40',
                  )}
                >
                  {variant.name[locale]} · {formatBdt(variant.price, locale)}
                </button>
              ))}
            </div>
          </section>
        )}

        {item.product.modifierGroups.map((group) => (
          <section key={group.id}>
            <h3 className="mb-3 font-semibold text-content text-xl">{group.name[locale]}</h3>
            <div className="flex flex-wrap gap-3">
              {group.options.map((option) => {
                const selected = item.modifierIds.includes(option.id)
                return (
                  <button
                    key={option.id}
                    type="button"
                    aria-pressed={selected}
                    onClick={() =>
                      dispatch({
                        type: 'toggle-modifier',
                        modifierId: option.id,
                        selection: group.selection,
                        groupOptionIds: group.options.map((each) => each.id),
                      })
                    }
                    className={cn(
                      'h-touch-kiosk rounded-xl border-2 px-6 font-medium text-lg',
                      selected
                        ? 'border-momo-yellow bg-momo-yellow/15 text-content'
                        : 'border-surface-border text-content-secondary',
                    )}
                  >
                    {option.name[locale]}
                    {option.priceDelta > 0 && ` +${formatBdt(option.priceDelta, locale)}`}
                  </button>
                )
              })}
            </div>
          </section>
        ))}

        <section>
          <h3 className="mb-3 font-semibold text-content text-xl">{t('customize.specialNote')}</h3>
          <textarea
            value={item.note ?? ''}
            onChange={(event) =>
              dispatch({ type: 'set-customize-note', note: event.target.value || null })
            }
            placeholder={t('customize.notePlaceholder')}
            maxLength={200}
            className="w-full rounded-xl border border-surface-border bg-surface-raised p-4 text-content text-lg"
            rows={2}
          />
        </section>
      </div>

      <footer className="flex items-center gap-4 border-surface-border border-t p-4">
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            size="lg"
            aria-label={t('common.quantity')}
            onClick={() =>
              dispatch({ type: 'set-customize-quantity', quantity: item.quantity - 1 })
            }
          >
            <Minus aria-hidden="true" className="size-5" />
          </Button>
          <span className="w-12 text-center font-bold text-2xl text-content tabular">
            {item.quantity}
          </span>
          <Button
            variant="secondary"
            size="lg"
            aria-label={t('common.quantity')}
            onClick={() =>
              dispatch({ type: 'set-customize-quantity', quantity: item.quantity + 1 })
            }
          >
            <Plus aria-hidden="true" className="size-5" />
          </Button>
        </div>

        <Button size="kiosk" fullWidth onClick={() => dispatch({ type: 'confirm-customize' })}>
          {t('kiosk.addToCart')} · {formatBdt(unitPrice * item.quantity, locale)}
        </Button>
      </footer>
    </main>
  )
}

function CartScreen({
  session,
  locale,
  t,
  totals,
  dispatch,
  onPay,
}: {
  session: KioskSession
  locale: Locale
  t: Translator
  totals: { subtotal: number; vat: number; total: number; itemCount: number }
  dispatch: (action: KioskAction) => void
  onPay: () => void
}) {
  return (
    <main className="flex min-h-dvh flex-col">
      <header className="flex items-center gap-3 border-surface-border border-b p-4">
        <Button variant="ghost" size="lg" onClick={() => dispatch({ type: 'go', screen: 'menu' })}>
          <ChevronLeft aria-hidden="true" className="size-6" />
          {t('common.back')}
        </Button>
        <h2 className="font-bold text-2xl text-content">{t('kiosk.yourOrder')}</h2>
      </header>

      <ul className="flex-1 divide-y divide-surface-border overflow-y-auto">
        {session.lines.map((line) => (
          <li key={line.key} className="flex items-center gap-4 p-4">
            <div className="flex-1">
              <p className="font-semibold text-content text-xl">{line.product.name[locale]}</p>
              {line.note && <p className="text-content-muted">{line.note}</p>}
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="secondary"
                aria-label={`${t('common.quantity')} −`}
                onClick={() =>
                  dispatch({ type: 'set-quantity', key: line.key, quantity: line.quantity - 1 })
                }
              >
                <Minus aria-hidden="true" className="size-4" />
              </Button>
              <span className="w-10 text-center font-bold text-content text-xl tabular">
                {line.quantity}
              </span>
              <Button
                variant="secondary"
                aria-label={`${t('common.quantity')} +`}
                onClick={() =>
                  dispatch({ type: 'set-quantity', key: line.key, quantity: line.quantity + 1 })
                }
              >
                <Plus aria-hidden="true" className="size-4" />
              </Button>
            </div>

            <span className="w-28 text-right font-bold text-content text-xl">
              {formatBdt(line.product.price * line.quantity, locale)}
            </span>
          </li>
        ))}
      </ul>

      <footer className="border-surface-border border-t p-6">
        <dl className="mb-4 space-y-2 text-lg">
          <div className="flex justify-between text-content-secondary">
            <dt>{t('common.subtotal')}</dt>
            <dd>{formatBdt(totals.subtotal, locale)}</dd>
          </div>
          <div className="flex justify-between text-content-secondary">
            <dt>{t('common.vat')}</dt>
            <dd>{formatBdt(totals.vat, locale)}</dd>
          </div>
          <div className="flex justify-between font-bold text-2xl text-content">
            <dt>{t('common.total')}</dt>
            <dd>{formatBdt(totals.total, locale)}</dd>
          </div>
        </dl>

        <Button size="kiosk" fullWidth onClick={onPay}>
          {t('kiosk.checkout')}
        </Button>
      </footer>
    </main>
  )
}

/**
 * FR-KIO-5 — payment.
 *
 * Launch behaviour is pay-at-counter (ADR-7): the kiosk issues an order number
 * and the cashier confirms the money. The method buttons are shown so the
 * customer knows what the counter accepts, and so the screen does not need a
 * redesign when a real gateway lands.
 */
function PaymentScreen({
  t,
  locale,
  total,
  isSubmitting,
  error,
  onBack,
  onConfirm,
}: {
  t: Translator
  locale: Locale
  total: number
  isSubmitting: boolean
  error: string | null
  onBack: () => void
  onConfirm: () => void
}) {
  return (
    <main className="flex min-h-dvh flex-col">
      <header className="flex items-center gap-3 border-surface-border border-b p-4">
        <Button variant="ghost" size="lg" onClick={onBack}>
          <ChevronLeft aria-hidden="true" className="size-6" />
          {t('common.back')}
        </Button>
        <h2 className="font-bold text-2xl text-content">{t('payment.choose')}</h2>
      </header>

      <div className="flex flex-1 flex-col items-center justify-center gap-8 p-8 text-center">
        <p className="font-bold text-6xl text-content">{formatBdt(total, locale)}</p>

        <div className="flex flex-wrap justify-center gap-3">
          {(['cash', 'bkash', 'nagad', 'rocket', 'card'] as const).map((method) => (
            <span
              key={method}
              className="rounded-xl border border-surface-border px-6 py-3 text-content-secondary text-lg"
            >
              {t(`payment.${method}`)}
            </span>
          ))}
        </div>

        <p className="max-w-lg text-content-secondary text-xl">{t('payment.payAtCounterHelp')}</p>

        {error && (
          <p className="rounded-xl bg-momo-red/15 px-6 py-4 text-momo-red text-lg">
            {t(`errors.${error}`)}
          </p>
        )}
      </div>

      <footer className="border-surface-border border-t p-6">
        <Button size="kiosk" fullWidth isLoading={isSubmitting} onClick={onConfirm}>
          {t('payment.payAtCounter')}
        </Button>
      </footer>
    </main>
  )
}

function SuccessScreen({
  t,
  locale,
  orderNumber,
  waitSeconds,
  onDone,
}: {
  t: Translator
  locale: Locale
  orderNumber: string
  waitSeconds: number
  onDone: () => void
}) {
  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-8 p-12 text-center">
      <p className="text-3xl text-momo-yellow">{t('orderStatus.thankYou')}</p>

      <div>
        <p className="text-content-muted text-xl">{t('orderStatus.yourNumber')}</p>
        <p className="font-bold text-9xl text-momo-white tabular">{orderNumber}</p>
      </div>

      <p className="text-content-secondary text-2xl">
        {t('orderStatus.estimatedWait')}:{' '}
        {t('orderStatus.minutes', { count: Math.ceil(waitSeconds / 60) })}
      </p>

      <p className="max-w-xl text-content-secondary text-xl">{t('payment.payAtCounterHelp')}</p>

      <Button size="kiosk" variant="secondary" onClick={onDone}>
        {t('common.done')}
      </Button>

      <span className="sr-only">{locale}</span>
    </main>
  )
}
